// src/scene/CameraController.tsx
/**
 * Camera Controller
 *
 * OrbitControls clamped to ±30° azimuth (left/right) and ±30° polar
 * (up/down around horizontal). Pan + zoom disabled — Leva owns FOV/zoom/
 * near/far; OrbitControls owns position via orbit-around-target.
 */

import { useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import { PerspectiveCamera } from 'three'
import { useControls, folder } from 'leva'
import { DESKTOP_CAMERA } from './useResponsiveLayout'

const DEG = Math.PI / 180
const YAW_CLAMP = 15 * DEG   // ±15° left/right (azimuth)
const PITCH_CLAMP = 15 * DEG // ±15° up/down around horizontal (polar)

// ============================================================================
// CAMERA CONTROLLER
// ============================================================================

export function CameraController() {
  const { camera } = useThree()
  const controlsRef = useRef<OrbitControlsImpl>(null)

  // Orbit pivot is centered on the avatar (X=0, Z=1.5 matches Avatar3D's
  // sceneRotationY-corrected position). Y=0.6 puts the pivot near the
  // throat/upper chest so the head + shoulders stay framed when orbiting.
  const {
    targetX, targetY, targetZ,
    fov, near, far, zoom,
  } = useControls('Camera', {
    Target: folder({
      targetX: { value: 0,   min: -2, max: 2, step: 0.001 },
      targetY: { value: 0.6, min: 0,  max: 2, step: 0.001 },
      targetZ: { value: 1.5, min: 0,  max: 4, step: 0.001 },
    }),
    Lens: folder({
      fov: { value: 28, min: 10, max: 120, step: 0.5 },
      zoom: { value: 1, min: 0.25, max: 4, step: 0.01, label: 'zoom' },
      near: { value: DESKTOP_CAMERA.near, min: 0.01, max: 10, step: 0.001 },
      far: { value: DESKTOP_CAMERA.far, min: 10, max: 10000, step: 1 },
    }),
  })

  // Lens-only lerp: FOV / zoom / near / far. Position + lookAt belong to OrbitControls.
  useFrame((_state, delta) => {
    const t = 1 - Math.exp(-4 * delta)
    if ('fov' in camera) {
      const persp = camera as PerspectiveCamera
      persp.fov += (fov - persp.fov) * t
      persp.zoom += (zoom - persp.zoom) * t
      persp.near = near
      persp.far = far
      persp.updateProjectionMatrix()
    }
  })

  return (
    <OrbitControls
      ref={controlsRef}
      target={[targetX, targetY, targetZ]}
      enablePan={false}
      enableZoom={false}
      enableDamping
      dampingFactor={0.08}
      rotateSpeed={0.5}
      minAzimuthAngle={-YAW_CLAMP}
      maxAzimuthAngle={YAW_CLAMP}
      minPolarAngle={Math.PI / 2 - PITCH_CLAMP}
      maxPolarAngle={Math.PI / 2 + PITCH_CLAMP}
    />
  )
}

// ============================================================================
// COMBINED CAMERA SYSTEM
// ============================================================================

export function CameraSystem() {
  return <CameraController />
}

export default CameraController
